import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCampaigns,
  createCampaign,
  STATE_BADGE_COLOR,
  type CampaignListItem,
} from "../../api/campaigns";
import { useIsMobile } from "../../lib/useMediaQuery";

export function CampaignsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: fetchCampaigns,
    refetchInterval: 5000,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");

  const createMut = useMutation({
    mutationFn: () =>
      createCampaign({
        customer_id: "00000000-0000-0000-0000-000000000001",
        display_name: name,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      setShowCreate(false);
      setName("");
      navigate(`/campaigns/${res.record.campaign_id}`);
    },
  });

  const campaigns = data?.items ?? [];
  const isMobile = useIsMobile();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
          Campaigns
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            background: "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-input)",
            padding: "8px 18px",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          + New Campaign
        </button>
      </div>

      {showCreate && (
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-card)",
          padding: 20,
          marginBottom: 20,
          display: "flex",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Campaign name"
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "var(--radius-input)",
                border: "1px solid var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: 14,
              }}
            />
          </div>
          <button
            onClick={() => createMut.mutate()}
            disabled={!name || createMut.isPending}
            style={{
              background: "var(--accent)",
              color: "#fff",
              border: "none",
              borderRadius: "var(--radius-input)",
              padding: "10px 18px",
              minHeight: "var(--tap-min)",
              fontSize: 14,
              cursor: "pointer",
              opacity: !name ? 0.5 : 1,
            }}
          >
            Create
          </button>
          <button
            onClick={() => setShowCreate(false)}
            style={{
              background: "transparent",
              color: "var(--text-secondary)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-input)",
              padding: "10px 18px",
              minHeight: "var(--tap-min)",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {isLoading ? (
        <p style={{ color: "var(--text-muted)" }}>Loading...</p>
      ) : campaigns.length === 0 ? (
        <p style={{ color: "var(--text-muted)" }}>No campaigns yet.</p>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {campaigns.map((c) => (
            <CampaignCard key={c.campaign_id} campaign={c} onClick={() => navigate(`/campaigns/${c.campaign_id}`)} />
          ))}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left" }}>
              <th style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Name</th>
              <th style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>State</th>
              <th style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Progress</th>
              <th style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <CampaignRow key={c.campaign_id} campaign={c} onClick={() => navigate(`/campaigns/${c.campaign_id}`)} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CampaignRow({ campaign: c, onClick }: { campaign: CampaignListItem; onClick: () => void }) {
  const pct = c.targets_total > 0 ? Math.round(((c.targets_completed + c.targets_failed) / c.targets_total) * 100) : 0;
  return (
    <tr onClick={onClick} style={{ cursor: "pointer", borderBottom: "1px solid var(--border-subtle)" }}>
      <td style={{ padding: "12px", color: "var(--text-primary)", fontWeight: 500 }}>{c.display_name}</td>
      <td style={{ padding: "12px" }}>
        <span style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: STATE_BADGE_COLOR[c.state] + "18",
          color: STATE_BADGE_COLOR[c.state],
          textTransform: "uppercase",
          letterSpacing: "0.03em",
        }}>
          {c.state}
        </span>
      </td>
      <td style={{ padding: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 80, height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "var(--success)", borderRadius: 3 }} />
          </div>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {c.targets_completed}/{c.targets_total}
          </span>
        </div>
      </td>
      <td style={{ padding: "12px", color: "var(--text-secondary)", fontSize: 13 }}>
        {new Date(c.created_at).toLocaleDateString()}
      </td>
    </tr>
  );
}

function CampaignCard({ campaign: c, onClick }: { campaign: CampaignListItem; onClick: () => void }) {
  const pct = c.targets_total > 0 ? Math.round(((c.targets_completed + c.targets_failed) / c.targets_total) * 100) : 0;
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <span style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>{c.display_name}</span>
        <span style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: STATE_BADGE_COLOR[c.state] + "18",
          color: STATE_BADGE_COLOR[c.state],
          textTransform: "uppercase",
          letterSpacing: "0.03em",
          whiteSpace: "nowrap",
        }}>
          {c.state}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--success)", borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
          {c.targets_completed}/{c.targets_total}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Created {new Date(c.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}
